import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import file from '../../../../assets/allInvoice-assets/file.svg';
import { lightPalette } from '../../../../theme';
import {
  exportAllDocumentToJSON,
  exportAllDocumentToXML,
  exportSelectedDocumentsToJSON,
  exportSelectedDocumentsToXML,
} from '../../../../services/allInvoiceApi';
import upload from '../../../../assets/allInvoice-assets/upload.svg';
import {
  ExportDocumentListItem,
  ExportDocumentPopover,
  classes,
} from './utils';
import CustomTooltip from '../../utils/CustomTooltip';
import { DOCUMENT_ACTION_TOOLTIP } from '../../../../constants/tooltipConstant';

const ExportDocument = ({
  gridApi,
  allRowSelected,
  searchValue,
  customFilterValue,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [documentExportDialogOpen, setDocumentExportDialogOpen] =
    useState(false);

  const handleOpenExportDialog = (event) => {
    setAnchorEl(event.currentTarget);
    setDocumentExportDialogOpen(true);
  };

  const handleCloseExportDialog = () => {
    setAnchorEl(null);
    setDocumentExportDialogOpen(false);
  };

  const handleExportToJSON = async () => {
    const selectedNodes = gridApi.getSelectedNodes();
    const filterModel = gridApi.getFilterModel();
    try {
      const selectedRowIds = selectedNodes.map((node) => node.data.id);
      const res = allRowSelected
        ? await exportAllDocumentToJSON(
            searchValue,
            filterModel,
            customFilterValue,
          )
        : await exportSelectedDocumentsToJSON(selectedRowIds);
      if (res) {
        const fileName = 'export-invoice.json';
        const jsonString = JSON.stringify(res);
        const blob = new Blob([jsonString], {
          type: 'text/plain;charset=utf-8',
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        link.click();
        URL.revokeObjectURL(url);
        handleCloseExportDialog();
      }
    } catch (error) {
      return error;
    }
  };

  const handleExportToXML = async () => {
    const selectedNodes = gridApi.getSelectedNodes();
    const filterModel = gridApi.getFilterModel();
    try {
      const selectedRowIds = selectedNodes.map((node) => node.data.id);
      const res = allRowSelected
        ? await exportAllDocumentToXML(
            searchValue,
            filterModel,
            customFilterValue,
          )
        : await exportSelectedDocumentsToXML(selectedRowIds);
      if (res) {
        const xmlData = res.data.data;
        const fileName = 'export-invoice.xml';
        if (xmlData) {
          const blob = new Blob([xmlData], {
            type: 'application/xml; charset=utf-8',
          });

          // Create a download link or trigger a download automatically
          const link = document.createElement('a');
          link.href = URL.createObjectURL(blob);
          link.download = fileName;
          link.click();

          // Revoke the object URL to avoid memory leaks
          URL.revokeObjectURL(link.href);
          handleCloseExportDialog();
        }
      }
    } catch (error) {
      return error;
    }
  };

  const ExportTypeDocument = [
    {
      itemText: 'Export to JSON',
      function: handleExportToJSON,
    },
    {
      itemText: 'Export to XML',
      function: handleExportToXML,
    },
  ];

  return (
    <>
      <CustomTooltip title={DOCUMENT_ACTION_TOOLTIP.EXPORT}>
        <Box
          component="img"
          src={upload}
          alt="export"
          className={classes.actionIcons}
          onClick={handleOpenExportDialog}
        />
      </CustomTooltip>
      <ExportDocumentPopover
        open={documentExportDialogOpen}
        anchorEl={anchorEl}
        onClose={handleCloseExportDialog}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <List>
          {ExportTypeDocument?.map((element) => (
            <ExportDocumentListItem
              disablePadding
              key={element.itemText}
              onClick={element.function}
            >
              <ListItemButton>
                <ListItemIcon>
                  <Box component="img" src={file} alt="export file" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography
                      variant="subtitle1"
                      color={lightPalette.shoeWax.darker}
                      fontWeight={500}
                      ml={2}
                    >
                      {element.itemText}
                    </Typography>
                  }
                />
              </ListItemButton>
            </ExportDocumentListItem>
          ))}
        </List>
      </ExportDocumentPopover>
    </>
  );
};

ExportDocument.defaultProps = {
  gridApi: null,
};

ExportDocument.propTypes = {
  gridApi: PropTypes.shape({
    getSelectedNodes: PropTypes.func.isRequired,
    getFilterModel: PropTypes.func.isRequired,
  }),
  allRowSelected: PropTypes.bool.isRequired,
  searchValue: PropTypes.string.isRequired,
  customFilterValue: PropTypes.shape({}).isRequired,
};

export default ExportDocument;
